import { EventSourceAdapter } from './EventSourceAdapter';
import { CONNECTION_MANAGER_EVENTS, EVENT_SOURCE_EVENTS, TIMEOUTS } from '~/composables/mercure/shared/constants';
import { createTimeoutManager } from '~/composables/mercure/shared/utils/TimeoutManager';
import type { ConnectionConfig, ConnectionState, EventListener } from '~/composables/mercure';
import type { TimeoutId, TimeoutManager } from '~/composables/mercure/shared/utils/TimeoutManager';

export class ConnectionManager {
  private eventSourceAdapter: EventSourceAdapter | null = null;
  private currentState: ConnectionState = { status: 'disconnected' };
  private currentConfig: ConnectionConfig | null = null;
  private retryTimeoutId: TimeoutId | null = null;
  private connectionPromise: Promise<void> | null = null;
  private readonly eventListeners: Map<string, Set<EventListener>>;
  private readonly timeoutManager: TimeoutManager;

  constructor () {
    this.eventListeners = new Map();
    this.timeoutManager = createTimeoutManager();
  }

  getState (): ConnectionState {
    return this.currentState;
  }

  getCurrentConfig (): ConnectionConfig | null {
    return this.currentConfig;
  }

  async connect (config: ConnectionConfig): Promise<void> {
    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    this.connectionPromise = this.executeConnect(config);

    try {
      await this.connectionPromise;
    } finally {
      this.connectionPromise = null;
    }
  }

  disconnect (): void {
    this.connectionPromise = null;

    if (this.retryTimeoutId) {
      this.timeoutManager.clearTimeout(this.retryTimeoutId);
      this.retryTimeoutId = null;
    }

    this.timeoutManager.clearAll();

    if (this.eventSourceAdapter) {
      this.eventSourceAdapter.close();
      this.eventSourceAdapter = null;
    }

    this.currentConfig = null;
    this.setState({ status: 'disconnected' });
  }

  isConnected (): boolean {
    return this.eventSourceAdapter?.isConnected() ?? false;
  }

  onStateChange (listener: (state: ConnectionState) => void): () => void {
    return this.on(CONNECTION_MANAGER_EVENTS.STATE_CHANGE, listener);
  }

  onMessage (listener: (event: MessageEvent) => void): () => void {
    return this.on(CONNECTION_MANAGER_EVENTS.MESSAGE, listener);
  }

  onError (listener: (error: Error) => void): () => void {
    return this.on(CONNECTION_MANAGER_EVENTS.ERROR, listener);
  }

  private async executeConnect (config: ConnectionConfig): Promise<void> {
    this.currentConfig = config;

    const url: string = this.buildMercureUrl(config);
    const headers: { Authorization: string } = {
      'Authorization': `Bearer ${config.token}`,
    };

    try {
      this.setState({ status: 'connecting', attempt: 0 });

      if (!this.eventSourceAdapter) {
        this.eventSourceAdapter = new EventSourceAdapter();
        this.setupAdapterListeners();
      }

      await this.eventSourceAdapter.connect({ url, headers });

      await this.waitForConnection();
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.setState({
        status: 'failed',
        error: err,
        attempts: 0,
      });

      this.emit(CONNECTION_MANAGER_EVENTS.ERROR, err);

      throw error;
    }
  }

  private on<T = any>(event: string, listener: EventListener<T>): () => void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set());
    }

    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.add(listener);
    }

    return (): void => {
      this.eventListeners.get(event)?.delete(listener);
    };
  }

  private emit<T = any>(event: string, data: T): void {
    const listeners: Set<EventListener> = this.eventListeners.get(event);
    if (!listeners) {
      return;
    }

    for (const listener of listeners) {
      try {
        listener(data);
      } catch (error) {
        console.error(`[ConnectionManager] Event listener error:`, error);
      }
    }
  }

  private setState (newState: ConnectionState): void {
    this.currentState = newState;
    this.emit(CONNECTION_MANAGER_EVENTS.STATE_CHANGE, newState);
  }

  private buildMercureUrl (config: ConnectionConfig): string {
    if (!config.hubUrl) {
      throw new Error('Mercure hub URL is required');
    }

    let url: URL;

    if (config.hubUrl.startsWith('http://') || config.hubUrl.startsWith('https://')) {
      url = new URL(config.hubUrl);
    } else {
      const runtimeConfig = useRuntimeConfig();
      const apiBaseUrl = (runtimeConfig.public.apiBaseUrl || runtimeConfig.public.apiRootUrl) as string;
      if (!apiBaseUrl) {
        throw new Error('API base URL not configured in runtime config');
      }
      url = new URL(config.hubUrl, apiBaseUrl);
    }

    config.topics.forEach((topic): void => {
      url.searchParams.append('topic', topic);
    });

    if (config.lastEventId) {
      url.searchParams.append('Last-Event-ID', config.lastEventId);
    }

    return url.toString();
  }

  private setupAdapterListeners (): void {
    if (!this.eventSourceAdapter) {
      return;
    }

    this.eventSourceAdapter.on(EVENT_SOURCE_EVENTS.OPEN, () => {
      this.setState({ status: 'connected', since: new Date() });
    });

    this.eventSourceAdapter.on(EVENT_SOURCE_EVENTS.MESSAGE, (event: MessageEvent) => {
      const enrichedEvent = event as MessageEvent & { subscribedTopics?: string[] };
      if (this.currentConfig) {
        enrichedEvent.subscribedTopics = [...this.currentConfig.topics];
      }
      this.emit(CONNECTION_MANAGER_EVENTS.MESSAGE, enrichedEvent);
    });

    this.eventSourceAdapter.on(EVENT_SOURCE_EVENTS.ERROR, (error: Error) => {
      this.emit(CONNECTION_MANAGER_EVENTS.ERROR, error);
    });

    this.eventSourceAdapter.on(EVENT_SOURCE_EVENTS.CLOSE, () => {
      if (this.currentState.status !== 'disconnected') {
        this.setState({ status: 'reconnecting', attempt: 0 });
      }
    });
  }

  private waitForConnection (timeout: number = TIMEOUTS.CONNECTION_TIMEOUT): Promise<void> {
    return new Promise((resolve, reject): void => {
      const timeoutId: TimeoutId = this.timeoutManager.setTimeout((): void => {
        reject(new Error('Connection timeout'));
      }, timeout);

      if (!this.eventSourceAdapter) {
        this.timeoutManager.clearTimeout(timeoutId);
        reject(new Error('No EventSource adapter'));
        return;
      }

      const unsubscribe = this.eventSourceAdapter.on('open', (): void => {
        this.timeoutManager.clearTimeout(timeoutId);
        unsubscribe();
        resolve();
      });

      const unsubscribeError = this.eventSourceAdapter.on('error', (error: Error): void => {
        this.timeoutManager.clearTimeout(timeoutId);
        unsubscribe();
        unsubscribeError();
        reject(error);
      });

      if (this.eventSourceAdapter.isConnected()) {
        this.timeoutManager.clearTimeout(timeoutId);
        unsubscribe();
        unsubscribeError();
        resolve();
      }
    });
  }
}
